import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Link, Type, Image, Music, Play, Video, Volume2, Share2 } from 'lucide-react'
import { useCardStore } from '../store/cardStore'
import type { CardType } from '../types'
import { useState } from 'react'

interface AddCardModalProps {
  isOpen: boolean
  onClose: () => void
}

const CARD_OPTIONS: { type: CardType; label: string; icon: React.ReactNode; description: string }[] = [
  { type: 'profile', label: 'Profile', icon: <User size={20} />, description: 'Name, nickname, and social links' },
  { type: 'link', label: 'Link', icon: <Link size={20} />, description: 'Button to custom URL' },
  { type: 'text', label: 'Text', icon: <Type size={20} />, description: 'Custom text content' },
  { type: 'photo', label: 'Photo', icon: <Image size={20} />, description: 'Single photo' },
  { type: 'audio', label: 'Audio', icon: <Volume2 size={20} />, description: 'Upload an audio clip' },
  { type: 'spotify_top', label: 'Top Songs', icon: <Music size={20} />, description: 'Your top listened songs' },
  { type: 'spotify_current', label: 'Now Playing', icon: <Play size={20} />, description: 'Currently playing track' },
  { type: 'video', label: 'Video', icon: <Video size={20} />, description: 'Embedded video' },
  { type: 'social_icon', label: 'Social Icon', icon: <Share2 size={20} />, description: 'Single social media icon' },
]

export default function AddCardModal({ isOpen, onClose }: AddCardModalProps) {
  const { addCard } = useCardStore()
  const [limitError, setLimitError] = useState<string>('')

  const handleAddCard = async (type: CardType) => {
    try {
      await addCard(type)
      onClose()
    } catch (error) {
      console.error('Failed to add card:', error)
      const e = error as any
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Failed to add card'
      setLimitError(String(msg))
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/80"
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-lg bg-nok-gray-900 border-t border-nok-gray-700 p-4 max-h-[70vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg font-bold text-nok-white">Add Card</h2>
              <button
                onClick={onClose}
                className="p-2 text-nok-gray-400 hover:text-nok-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {CARD_OPTIONS.map((option) => (
                <motion.button
                  key={option.type}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAddCard(option.type)}
                  className="p-4 bg-nok-gray-800 border border-nok-gray-700 text-left hover:border-nok-gray-500 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2 text-nok-white">
                    {option.icon}
                    <span className="font-serif font-bold">{option.label}</span>
                  </div>
                  <p className="font-serif text-xs text-nok-gray-400">{option.description}</p>
                </motion.button>
              ))}
            </div>

            <AnimatePresence>
              {limitError && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
                  onClick={() => setLimitError('')}
                >
                  <motion.div
                    initial={{ scale: 0.98, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.98, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="w-full max-w-sm bg-nok-gray-900 border border-nok-gray-700 p-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-serif text-sm font-bold text-nok-white">Card limit</h3>
                      <button
                        type="button"
                        onClick={() => setLimitError('')}
                        className="text-nok-gray-400 hover:text-nok-white transition-colors"
                        aria-label="Close"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <p className="font-serif text-sm text-nok-gray-300 leading-relaxed">{limitError}</p>
                    <button
                      type="button"
                      onClick={() => setLimitError('')}
                      className="mt-4 w-full py-2 bg-nok-gray-800 border border-nok-gray-700 text-nok-white font-serif text-sm hover:border-nok-gray-500 transition-colors"
                    >
                      OK
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
